import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Lead } from '@/shared/types'
import { apiFetch } from '@/shared/api/core'
import { spliceLeadIntoCache } from '@/shared/api/leadLists'
import { useCanEdit } from '@/shared/write-access'

// ── notes (comment thread on a lead) ──────────────────────────────────────

// Notes are the one write path that did not run through useLeadMutations, so
// they missed its read-only guard: an admin watching a colleague's queue could
// still create, edit and delete notes on their leads. Guarding here rather than
// per component covers every caller, including ones added later.
class ReadOnlyError extends Error {
  constructor() { super('Read-only — turn on edit mode first.') }
}

interface CreateNoteVars { leadId: string; body: string }
interface PatchNoteVars  { leadId: string; noteId: string; body: string }
interface DeleteNoteVars { leadId: string; noteId: string }

// POST /api/leads/{id}/notes — append a comment. Server stamps
// id / authorEmail / createdAt / updatedAt and returns the refreshed Lead.
export function useCreateNote() {
  const qc = useQueryClient()
  const canEdit = useCanEdit()
  return useMutation({
    mutationFn: ({ leadId, body }: CreateNoteVars) =>
      canEdit
        ? apiFetch<Lead>(`/api/leads/${leadId}/notes`, { method: 'POST', body: { body } })
        : Promise.reject(new ReadOnlyError()),
    onSuccess: (lead) => {
      spliceLeadIntoCache(qc, lead)
    },
  })
}

// PATCH /api/leads/{id}/notes/{noteId} — author-only edit. Anyone else
// gets a 404 (existence is hidden — see notes_service.update_note).
export function usePatchNote() {
  const qc = useQueryClient()
  const canEdit = useCanEdit()
  return useMutation({
    mutationFn: ({ leadId, noteId, body }: PatchNoteVars) =>
      canEdit
        ? apiFetch<Lead>(`/api/leads/${leadId}/notes/${noteId}`, {
            method: 'PATCH',
            body: { body },
          })
        : Promise.reject(new ReadOnlyError()),
    onSuccess: (lead) => {
      spliceLeadIntoCache(qc, lead)
    },
  })
}

// DELETE /api/leads/{id}/notes/{noteId} — author-only (plus legacy-
// authored notes, which any BDR can clean up).
export function useDeleteNote() {
  const qc = useQueryClient()
  const canEdit = useCanEdit()
  return useMutation({
    mutationFn: ({ leadId, noteId }: DeleteNoteVars) =>
      canEdit
        ? apiFetch<Lead>(`/api/leads/${leadId}/notes/${noteId}`, { method: 'DELETE' })
        : Promise.reject(new ReadOnlyError()),
    onSuccess: (lead) => {
      spliceLeadIntoCache(qc, lead)
    },
  })
}
